import type {
  OnboardingCatalogDto,
  OnboardingStatusDto,
  OnboardingSubmitRequest,
  DiscoveryConfirmRequest,
  PaymentProcessorKind,
} from '@signalvote/shared';
import { apiJson, apiVoid } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';
import { refusal } from './useFinanceContributions';
import { FINANCE_STALE_TIME } from './queryUtils';
import { useRefreshFinanceAfter } from './useFinanceSettings';

/**
 * The single source of truth for "finance setup isn't finished yet". Setup is
 * incomplete when EITHER a bank OR a payment processor is still missing — the
 * `||` (not `&&`) is deliberate: the checklist framing keeps the nudge alive
 * until BOTH exist. `undefined` (status still loading) is treated as NOT
 * incomplete so a cold load never flashes a false "!". Both the Settings-tab
 * indicator (`FinanceLayout`) and the dashboard setup card (`FinanceDashboard`)
 * read this so the two surfaces can never drift out of sync.
 */
export function isOnboardingIncomplete(
  // Only the two counts drive the predicate — narrowed so callers (and tests)
  // don't have to synthesize the rest of the onboarding DTO.
  status: Pick<OnboardingStatusDto, 'bankCount' | 'processorCount'> | undefined,
): boolean {
  if (!status) return false;
  return status.bankCount === 0 || status.processorCount === 0;
}

// ============================================================================
// Reads
// ============================================================================

const onboardingCatalogRead = defineQuery({
  key: () => ['finance', 'onboarding', 'catalog'],
  reads: 'GET /api/finance/onboarding/catalog',
  // The pre-shipped banks and the processor enum barely move; a bank profile write refreshes it.
  staleTime: 60 * 60 * 1000,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<OnboardingCatalogDto>('/api/finance/onboarding/catalog', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch onboarding catalog',
    }),
});

export function useOnboardingCatalog() {
  return onboardingCatalogRead(undefined);
}

const onboardingStatusRead = defineQuery({
  key: () => ['finance', 'onboarding', 'status'],
  reads: 'GET /api/finance/onboarding/status',
  staleTime: FINANCE_STALE_TIME,
  fetch: (_: undefined, { signal, campaignId }) =>
    apiJson<OnboardingStatusDto>('/api/finance/onboarding/status', {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch onboarding status',
    }),
});

export function useOnboardingStatus() {
  return onboardingStatusRead(undefined);
}

/** The onboarding status once its read has answered; undefined while it loads or could not answer. */
export function readyOnboardingStatus(read: ReturnType<typeof useOnboardingStatus>): OnboardingStatusDto | undefined {
  return read.status === 'ready' ? read.data : undefined;
}

// ============================================================================
// Writes
//
// Each saves through the request, so its write set refreshes the onboarding reads. Submit and
// discovery set up banks, processors and the finance settings that the processor-fee donations,
// manual P2P deposits and report previews read, which are not defined reads yet, so those two also
// refresh the undeclared finance reads.
// ============================================================================

const submitOnboarding = defineWrite({
  send: (data: OnboardingSubmitRequest) =>
    apiJson<OnboardingStatusDto>('/api/finance/onboarding/submit', {
      method: 'POST',
      json: data,
      error: refusal('Failed to save onboarding'),
    }),
});

export function useSubmitOnboarding() {
  return useRefreshFinanceAfter(submitOnboarding());
}

const confirmDiscovery = defineWrite({
  send: (data: DiscoveryConfirmRequest) =>
    apiJson<OnboardingStatusDto>('/api/finance/onboarding/discover', {
      method: 'POST',
      json: data,
      error: refusal('Failed to confirm discovery'),
    }),
});

export function useConfirmDiscovery() {
  return useRefreshFinanceAfter(confirmDiscovery());
}

// Unchecking a SAVED bank/processor removes it. Removal is hard-delete-when-empty on the backend;
// a bank/processor that still has imported data returns a 409 with a treasurer-readable message,
// which the failure carries. A processor removed while empty has no fee donations or deposits, so
// the write set covers every read it can change.

export const useRemoveOnboardingBank = defineWrite({
  send: (bankSourceProfileId: string) =>
    apiVoid(`/api/finance/onboarding/banks/${bankSourceProfileId}`, {
      method: 'DELETE',
      error: refusal('Failed to remove bank'),
    }),
});

export const useRemoveOnboardingProcessor = defineWrite({
  send: (kind: PaymentProcessorKind) =>
    apiVoid(`/api/finance/onboarding/processors/${kind}`, {
      method: 'DELETE',
      error: refusal('Failed to remove processor'),
    }),
});
